import * as i0 from '@angular/core';
import { Pipe, NgModule, Directive, isDevMode, Injectable, Optional, Inject } from '@angular/core';
import { get, filter, includes, find, isEmpty, merge, isString, debounce } from 'lodash-es';
import { throwError, timer, Subscription, BehaviorSubject, interval, Subject } from 'rxjs';
import { __decorate } from 'tslib';
import * as i1 from '@angular/common';
import * as i2 from '@upr-web-primer/storage';
import { By } from '@angular/platform-browser';
import { takeUntil } from 'rxjs/operators';
class PriceCentsPipe {
  constructor() {
    this.testText = new RegExp('[a-z|A-Z]', 'gi');
  }
  transform(price) {
    if (!price || this.testText.test(price)) {
      return '';
    }
    // Strip any content
    price = String(price).replace(/[^0-9.]/g, '');
    // if price contains period or does not contain text, return modified, else return nothing.
    if (price.includes('.')) {
      return '.' + (Number(price) % 1 || 0).toFixed(2).substr(2);
    } else {
      return '';
    }
  }
  static {
    this.ɵfac = function PriceCentsPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PriceCentsPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "priceCents",
      type: PriceCentsPipe,
      pure: true,
      standalone: false
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PriceCentsPipe, [{
    type: Pipe,
    args: [{
      name: 'priceCents',
      standalone: false
    }]
  }], null, null);
})();

/**
 * Use with environment objects
 *
 * @param {(boolean | undefined)} value
 * @param {boolean} fallback
 * @return {*}  {boolean}
 */
function checkForFallback(value, fallback) {
  return value === undefined ? fallback : value;
}
/**
 * Use only with udeploy objects
 *
 * @param {string} str
 * @return {*}  {string | undefined}
 */
function param(str) {
  return str.replace(/\$.*\$/g, '') || undefined;
}
/**
 * Use only with udeploy objects
 *
 * @param {string} str
 * @return {*}  {string | undefined}
 */
function paramNum(str) {
  return Number(param(str)) || undefined;
}
/**
 * Use only with udeploy objects
 *
 * @param {string} str
 * @return {*}  {(boolean | undefined)}
 */
function paramBool(str) {
  const fixedStr = param(str);
  if (fixedStr === undefined) {
    return undefined;
  }
  if (fixedStr === 'true') {
    return true;
  } else {
    return false;
  }
}
function paramArray(str) {
  const checkedStr = param(str);
  if (checkedStr === undefined) {
    return undefined;
  }
  const fixedStr = checkedStr.split(',');
  if (fixedStr.length > 0) {
    return fixedStr;
  } else {
    return undefined;
  }
}
const DEFAULT_RETRY_STRATEGY = {
  maxRetryAttempts: 3,
  scalingDuration: 500,
  excludedStatusCodes: [0, 403, 401],
  excludeHttpMessages: ['Forbidden', 'Unauthorized', 'Unknown Error']
};
function getValuesFromPath(path) {
  const findLocation = path.indexOf('?') + 1 || path.indexOf('#') + 1;
  let cleanData = '';
  if (findLocation) {
    cleanData = path.slice(findLocation);
  }
  if (!cleanData) {
    return {};
  }
  const paramsList = cleanData.split('&').reduce((prev, item) => {
    return Object.assign({
      [item.split('=')[0]]: item.split('=')[1]
    }, prev);
  }, {});
  return paramsList || {};
}
/**
 * Generic Retry Strategy for RXJS Retry function.
 *
 * @param {DEFAULT_RETRY_STRATEGY} retryObj
 * @return {*}  {RetryConfig}
 */
const genericRetryStrategy = (retryObj = DEFAULT_RETRY_STRATEGY) => {
  const newRetryObj = {
    ...DEFAULT_RETRY_STRATEGY,
    ...retryObj
  };
  return {
    count: newRetryObj.maxRetryAttempts,
    delay: (error, retryAttempt) => {
      if (error && retryAttempt > newRetryObj.maxRetryAttempts || newRetryObj.excludedStatusCodes.find(e => e === error?.status || error?.error && e === error?.error?.status) !== undefined && newRetryObj.excludeHttpMessages.find(m => m === error?.statusText || m === error?.error?.httpMessage)) {
        return throwError(() => error);
      }
      // Throw if error message present from endpoint
      if (get(error, 'error.errors[0].errorMessage')) {
        return throwError(() => error);
      }
      if (newRetryObj.customFunc) {
        return newRetryObj.customFunc(error, newRetryObj, retryAttempt);
      }
      return timer(retryAttempt * newRetryObj.scalingDuration);
    }
  };
};

/**
 * udeploy constants will be overwritten during deployment using
 * wildcards $foo$. Each environment will have own unique values
 * in udeploy configuration.
 *
 * For local development sets defaults using regex.
 */
const udeploy = {
  currency: {
    symbol: param('$default.currency$'),
    secondarySymbols: param('$default.secondarysymbols$'),
    bypass: param('$default.bypasscurrency$')
  }
};

// The file contents for the current environment will overwrite these during build.
// The build system defaults to the dev environment which uses `environment.ts`, but if you do
// `ng build --env=prod` then `environment.prod.ts` will be used instead.
// The list of which env maps to which file can be found in `.angular-cli.json`.
const environment = {
  currency: {
    symbol: udeploy.currency.symbol || '$',
    bypass: udeploy.currency.bypass || '',
    secondarySymbols: udeploy.currency.secondarySymbols || ''
  }
};
class PriceDollarsPipe {
  constructor() {
    this.testText = new RegExp('[a-z|A-Z]', 'gi');
    this.testCurrency = new RegExp('(\\d+.\\d+)', 'gi');
    this.bypassSymbols = environment.currency.bypass;
    this.ignoreSymbols = this.bypassSymbols.split(' ');
    this.currencySymbol = environment.currency.symbol;
    this.secondarySymbols = get(environment.currency, 'secondarySymbols', '').split('');
  }
  transform(price) {
    if (!price) {
      return price;
    }
    price = price.trim();
    const containsIgnoredSymbols = filter(this.ignoreSymbols, symbol => includes(price, symbol));
    if (!!containsIgnoredSymbols && containsIgnoredSymbols.length) {
      const expression = new RegExp('[^0-9.$. ' + this.bypassSymbols + this.currencySymbol + this.secondarySymbols + ']', 'gi');
      price = price.replace(expression, '');
      const containsSecondarySymbols = !!(find(this.secondarySymbols, symbol => includes(price, symbol)) || []).length;
      if (price.indexOf(this.currencySymbol) === -1 && price.indexOf(this.secondarySymbols) === -1 && !containsSecondarySymbols) {
        return this.currencySymbol + price;
      }
      const otherIgnorSymbols = containsIgnoredSymbols.filter(e => e !== ',');
      const hasCurrencySymbol = price.includes(this.currencySymbol);
      const hasSecondarySymbols = this.secondarySymbols.some(substring => price.includes(substring));
      if ((hasCurrencySymbol || hasSecondarySymbols) && otherIgnorSymbols.length) {
        return price;
      }
    }
    const testPrice = this.testText.test(price);
    // Might be price like '11.99' or might be text like '10.00; with grilled chicken 14.00'
    if (!testPrice) {
      if (typeof price === 'string' || price instanceof String) {
        price = +price.replace(/[^0-9.]/g, '');
      }
      return this.currencySymbol + Math.floor(price).toLocaleString();
    }
    // If not just price, add the currency infront of all prices.
    return price.replace(this.testCurrency, `${this.currencySymbol}$$&`);
  }
  static {
    this.ɵfac = function PriceDollarsPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PriceDollarsPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "priceDollars",
      type: PriceDollarsPipe,
      pure: true,
      standalone: false
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PriceDollarsPipe, [{
    type: Pipe,
    args: [{
      name: 'priceDollars',
      standalone: false
    }]
  }], null, null);
})();
const ignoreSymbols = environment.currency.bypass.split(' ');
const currencySymbol = environment.currency.symbol.split(' ');
const secondarySymbols = environment.currency.secondarySymbols.split('');
class PriceLegalMarksPipe {
  transform(price) {
    if (!price) {
      return '';
    }
    const expression = '[^0-9.$., ' + ignoreSymbols + currencySymbol + secondarySymbols + ']';
    const legal = String(price).match(new RegExp(expression, 'gi'));
    return legal ? legal.join('') : '';
  }
  static {
    this.ɵfac = function PriceLegalMarksPipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PriceLegalMarksPipe)();
    };
  }
  static {
    this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "priceLegalMarks",
      type: PriceLegalMarksPipe,
      pure: true,
      standalone: false
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PriceLegalMarksPipe, [{
    type: Pipe,
    args: [{
      name: 'priceLegalMarks',
      standalone: false
    }]
  }], null, null);
})();
class AppUtilsModule {
  static {
    this.ɵfac = function AppUtilsModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || AppUtilsModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: AppUtilsModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AppUtilsModule, [{
    type: NgModule,
    args: [{
      declarations: [PriceLegalMarksPipe, PriceDollarsPipe, PriceCentsPipe],
      imports: [],
      exports: []
    }]
  }], null, null);
})();
class BaseComponent {
  constructor(ngZone) {
    this.ngZone = ngZone;
  }
  ngOnInit() {}
  ngOnDestroy() {}
  static {
    this.ɵfac = function BaseComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || BaseComponent)(i0.ɵɵdirectiveInject(i0.NgZone));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: BaseComponent
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BaseComponent, [{
    type: Directive
  }], () => [{
    type: i0.NgZone
  }], null);
})();

// Tools
// lodash
/**
 * Auto setup up subscription variable within a component, with auto un-subscription on destroy.
 * @param {string} checkForAll if true, will check all properties for any subscriptions, and auto unsubscribe those too.
 * @ClassDecorator
 */
function AutoSubscription(options = {
  checkForAll: false
}, observables$ = []) {
  // Return decorator
  return function (sourceClass) {
    // Get original onInit method
    const onInit = sourceClass.prototype.ngOnInit;
    // Get original onDestroy method
    const onDestroy = sourceClass.prototype.ngOnDestroy;
    // If we got original ngOnDestroy
    if (typeof onDestroy === 'function') {
      // This function will create new Subscription instance
      const initFunction = () => {
        // Create new Subscription instance
        sourceClass.prototype['subscriptions'] = new Subscription();
      };
      // Create new Subscription instance if, and or not available.
      initFunction();
      // Replace OnInit method
      sourceClass.prototype.ngOnInit = function () {
        // Initialize onInit if no subscriptions exist
        if (isEmpty(this.subscriptions['_subscriptions'])) {
          initFunction();
        }
        // If we got original onInit method
        if (onInit && typeof onInit === 'function') {
          // Handle original onInit method
          onInit.apply(this, arguments);
        }
      };
      // Replace OnDestroy method
      sourceClass.prototype.ngOnDestroy = function () {
        // Unsubscribe all subscribers from subscriptions variable
        if (this.subscriptions && this.subscriptions.unsubscribe) {
          this.subscriptions.unsubscribe();
        }
        // Should we check for all methods and properties that are subscriptions and auto unsubscribe.
        if (options && options.checkForAll) {
          for (const prop in this) {
            const property = this[prop];
            if (typeof property.unsubscribe === 'function' && !observables$.includes(property)) {
              observables$.push(property);
            }
          }
          for (const observable$ of observables$) {
            observable$.unsubscribe();
          }
        }
        // If we got original onDestroy method
        if (onDestroy && typeof onDestroy === 'function') {
          // Handle original onDestroy method
          onDestroy.apply(this, arguments);
        }
        // Create again new Subscription instance as this breaks after first ngOnDestroy
        initFunction();
      };
      // Child class must have ngOnDestroy, even if empty
    } else {
      console.error('===========  ==========');
      // Throw error
      console.error(sourceClass.name, 'must have ngOnDestroy implemented within the class, even if empty');
      console.error('===========  ==========');
    }
    return sourceClass;
  };
}
let SubscriptionComponent = class SubscriptionComponent extends BaseComponent {
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵSubscriptionComponent_BaseFactory;
      return function SubscriptionComponent_Factory(__ngFactoryType__) {
        return (ɵSubscriptionComponent_BaseFactory || (ɵSubscriptionComponent_BaseFactory = i0.ɵɵgetInheritedFactory(SubscriptionComponent)))(__ngFactoryType__ || SubscriptionComponent);
      };
    })();
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: SubscriptionComponent,
      features: [i0.ɵɵInheritDefinitionFeature]
    });
  }
};
SubscriptionComponent = __decorate([AutoSubscription()], SubscriptionComponent);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SubscriptionComponent, [{
    type: Directive
  }], null, null);
})();

/**
 * Will only post in console if in dev mode, or has the local storage key of 'enableDevDebugConsole'
 * @param {string} className Class Identifier
 * @param {any} args any additional data you want to pass, including errors, messages, objects, etc.
 */
function devLogger(className, ...args) {
  if (isDevMode() || get(window.localStorage, 'enableDevDebugConsole')) {
    return console.warn(className, ...args);
  }
}

/**
 * Setup a service guard, that can check for a specific property, run a function, run a promise or automatically check using a method or property called "serviceGuard"
 * Currently only works with non-compiled angular code
 * @param {string} checkValueOverride check for specific value instead of "serviceGuard".
 * @param {string} appDefaultCheck check for specific values inside appDefaults instead of using "serviceGuard" methods.
 * @param {string} bypassFunction bypass check for these specific calls in this passed array
 * @ClassDecorator
 */
function ServiceGuard(options) {
  return function (sourceClass) {
    const proxy = new Proxy(sourceClass, {
      construct(oConstructor, args, target) {
        let error = '';
        const instance = Reflect.construct(oConstructor, args, target);
        instance.prototype = sourceClass.prototype;
        let serviceGuard = instance.prototype.serviceGuard;
        if (serviceGuard === undefined) {
          serviceGuard = instance.serviceGuard;
        }
        // This function takes care of setting up proxy, or ignoring proxy if check passes.
        const checkValue = function (check) {
          if (!check) {
            error = 'will not execute any functions except for bypass functions, and properties will stay read only, as service guard check was not a truthy value.';
            devLogger(sourceClass.name, error);
            const handler = {
              get: function (oClass, property, _args2) {
                let prop = Reflect.get(oClass, property);
                if (prop === undefined) {
                  prop = Reflect.get(sourceClass, property);
                }
                if (prop === undefined) {
                  prop = args[property];
                }
                if (options && options.bypassFunction && options.bypassFunction.includes(property)) {
                  return prop;
                }
                if (typeof prop === 'function') {
                  return () => Promise.resolve();
                }
                return prop;
              }
            };
            return new Proxy(instance, handler);
          }
          return instance;
        };
        // check for options if available
        if (options) {
          // If we only have checkValueOverride
          if (options.checkValueOverride && !options.appDefaultCheck) {
            if (instance.prototype[options.checkValueOverride] === undefined && instance[options.checkValueOverride] === undefined) {
              error = `needs a method or property named  "${options.checkValueOverride}" with a truthy return`;
            } else {
              // override with method or property to run the check below.
              if (instance.prototype[options.checkValueOverride] !== undefined) {
                serviceGuard = instance.prototype[options.checkValueOverride];
              }
              serviceGuard = instance[options.checkValueOverride];
            }
          }
          // Run through app defaults
          if (options.appDefaultCheck) {
            if (!instance['appDefaults']) {
              error = 'needs dependency called "appDefaults" to use appDefaultCheck';
            } else {
              let check = true;
              for (const key of options.appDefaultCheck) {
                if (!get(instance['appDefaults'], key, '')) {
                  check = false;
                }
              }
              return checkValue(check);
            }
          }
        }
        // continue with service guard check.
        if (serviceGuard !== undefined) {
          // Run as Function
          if (typeof serviceGuard === 'function') {
            const checkFunction = serviceGuard.apply(instance);
            if (typeof checkFunction === 'object' && checkFunction.then) {
              return checkFunction.then(value => {
                return checkValue(value);
              }).catch(() => {});
            }
            return checkValue(checkFunction);
          }
          // Run as Property
          return checkValue(serviceGuard);
        } else {
          error = 'needs a method or property named serviceGuard with a truthy return';
        }
        if (error) {
          devLogger(sourceClass.name, error + ', in order to determine if it can pass the guard.');
          return checkValue(false);
        }
      }
    });
    return proxy;
  };
}
const CacheKeys = {
  DEBUG_APP_ENV: 'DEBUG_APP_ENV',
  DEBUG_APP_ENV_RESET: 'DEBUG_APP_ENV_RESET',
  DEBUG_APP_ENV_CURRENT: 'DEBUG_APP_ENV_CURRENT',
  DEBUG_APP_ENV_SHARED: 'DEBUG_APP_ENV_SHARED',
  DEBUG_APP_ENV_SHARED_ALWAYS: 'DEBUG_APP_ENV_SHARED_ALWAYS',
  DEBUG_APP_ENV_KEY: 'DEBUG_APP_ENV_KEY'
};
const APP_ENV_REPLACED_DEFAULTS = {
  app_env: {
    content_id: param('$app_env.content_id$'),
    debug_key: param('$app_env.debug_key$')
  }
};
const APP_ENV_ENV_DEFAULTS = {
  app_env: {
    content_id: '',
    debug_key: ''
  }
};
class AppEnvironmentService {
  // Getter for envValues
  get envValues() {
    return this._envValues;
  }
  // Setter for envValues
  set envValues(value) {
    this._envValues = value;
    if (this.debugEnv) {
      if (!isEmpty(this.debugValues)) {
        // Since we have debug values, we still want debug values to override all others.
        this._envValues = merge(this._envValues, this.debugValues);
      }
      // Save current setup into localStorage to see current state after merge of values
      this.localStorage.setItem(CacheKeys.DEBUG_APP_ENV_CURRENT, this._envValues);
    }
    this.envValues$.next(this._envValues);
  }
  constructor(customEnv, locationStrategy, localStorage) {
    this.customEnv = customEnv;
    this.locationStrategy = locationStrategy;
    this.localStorage = localStorage;
    this.envValues$ = new BehaviorSubject({});
    if (!isEmpty(this.customEnv)) {
      this.setupEnvDefaults(this.customEnv);
    }
    this.addDefaultValues(APP_ENV_ENV_DEFAULTS, APP_ENV_REPLACED_DEFAULTS);
    this.checkDebugValues();
  }
  /**
   * Setup initial env values. Generally used in initialization of app.
   *
   * If CustomEnv injection token is passed in the providers, it will run
   * below function automatically.
   *
   * @param {Record<string, any>} defaults
   */
  setupEnvDefaults(defaults) {
    this._envValues = defaults;
  }
  /**
   * Get all env variables, or a subset, by key
   *
   * @param {string} [key]
   * @return {*}  {(T | any)}
   */
  getEnvVariables(key) {
    if (key && this._envValues[key]) {
      return this._envValues[key];
    }
    return this._envValues;
  }
  /**
   * This is generally used by the npm package to setup initial package values.
   *
   * @param {Record<string, any>} defaultObj
   * @param {Record<string, any>} udeployObj
   */
  addDefaultValues(defaultObj, udeployObj) {
    // First we merge custom values with defaults, custom env from app override defaults
    const overrideDefaults = merge(defaultObj, this._envValues);
    // Second we merge override from app, with values from udeploy
    const values = merge(overrideDefaults, udeployObj);
    // Apply package values
    this.envValues = values;
  }
  /**
   * This is used to dynamically merge a subset of values
   *
   * @param {Record<string, any>} values
   */
  mergeWithCurrentEnv(values) {
    this.envValues = merge(this._envValues, values);
  }
  resetEnvValues() {
    this.localStorage.removeItem('oAuthToken');
    this.localStorage.removeItem('oAuthRefreshToken');
    this.localStorage.removeItem('HEADERS_WC_TOKEN');
    this.localStorage.removeItem('HEADERS_WC_TRUSTED_TOKEN');
    this.localStorage.removeItem('InteractGuestId');
    this.localStorage.removeItem('STORE_ID');
  }
  checkDebugValues() {
    this.debugEnv = this.localStorage.getItem(CacheKeys.DEBUG_APP_ENV, true);
    const keys = this.localStorage.getItem(CacheKeys.DEBUG_APP_ENV_KEY, true) || {};
    const base = this.locationStrategy.getBaseHref();
    if (!get(keys, base)) {
      keys[base] = this._envValues.app_env.debug_key;
      if (this.debugEnv) {
        this.localStorage.setItem(CacheKeys.DEBUG_APP_ENV_KEY, keys);
      }
    } else {
      this._envValues.app_env.debug_key = keys[base];
    }
    const appKey = this._envValues.app_env.debug_key;
    if (this.debugEnv && appKey) {
      const debugReset = this.localStorage.getItem(CacheKeys.DEBUG_APP_ENV_RESET, true);
      if (debugReset) {
        this.resetEnvValues();
      }
      this.localStorage.setItem(CacheKeys.DEBUG_APP_ENV_RESET, false);
      this.appCacheKey = `${CacheKeys.DEBUG_APP_ENV}_${appKey}`;
      // If debug app env key is present, let's check if any shared values are present
      // These values generally are the ones setup through ENV_DEFAULTS for consistency
      const shared = this.localStorage.getItem(CacheKeys.DEBUG_APP_ENV_SHARED, true) || {};
      const sharedAlways = this.localStorage.getItem(CacheKeys.DEBUG_APP_ENV_SHARED_ALWAYS, true) || {};
      // Setup values if missing
      if (isEmpty(shared)) {
        this.localStorage.setItem(CacheKeys.DEBUG_APP_ENV_SHARED, {});
      }
      if (isEmpty(sharedAlways)) {
        this.localStorage.setItem(CacheKeys.DEBUG_APP_ENV_SHARED_ALWAYS, {});
      }
      const app = this.localStorage.getItem(this.appCacheKey, true);
      // Let's merge app into shared, app taking precedence.
      const mergedValues = merge(shared, app);
      // Values that always need to be the same
      const completedValues = merge(mergedValues, sharedAlways);
      this.debugValues = completedValues;
      this.localStorage.setItem(this.appCacheKey, completedValues);
    }
  }
  static {
    this.ɵfac = function AppEnvironmentService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || AppEnvironmentService)(i0.ɵɵinject('CustomEnv', 8), i0.ɵɵinject(i1.LocationStrategy), i0.ɵɵinject(i2.LocalStorageService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: AppEnvironmentService,
      factory: AppEnvironmentService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AppEnvironmentService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: ['CustomEnv']
    }]
  }, {
    type: i1.LocationStrategy
  }, {
    type: i2.LocalStorageService
  }], null);
})();

/**
 * Export JasmineBaseHelper
 */
class JasmineBaseHelper {
  /**
   * Constructor
   * @param {ComponentFixture} fixture
   * @param {function} expect
   * @param {function} spyOn
   */
  constructor(fixture, expect, spyOn) {
    this.fixture = fixture;
    this.de = this.fixture.debugElement;
    this.detectChanges();
    this.expect = expect;
    this.spy = spyOn;
  }
  /**
   * Spy method in component
   *    -: Install a spy onto an existing object.
   *        Exp. -> helper.getById(id).spyOn()
   *
   * @param {any} component
   * @param {string} method
   * @param args
   */
  spyOn(component, method, ...args) {
    this.spy(component, method).and.callThrough();
    component[method](...args);
    this.expect(component[method])[args.length > 0 ? 'toHaveBeenCalledWith' : 'toHaveBeenCalled'](...args);
  }
  /**
   * Test: To Be Close To
   *    -: expect the actual value to be within a specified precision of the expected value.
   *        Exp. -> helper.getById(id).toBeCloseTo(42.2, 3)
   *
   * @param {number} arg
   * @param {number} precision
   */
  toBeCloseTo(arg, precision) {
    this.doExpect('toBeCloseTo', this.getValue(false, true), false, false, arg, precision);
  }
  /**
   * Test: To Be Greater Than
   *    -: expect the actual value to be greater than the expected value.
   *        Exp. -> helper.getById(id).toBeGreaterThan(3)
   *
   * @param {number} arg
   */
  toBeGreaterThan(arg) {
    this.doExpect('toBeGreaterThan', this.getValue(false, true), false, false, arg);
  }
  /**
   * Test: To Be Greater Than Or Equal
   *    -: expect the actual value to be greater than or equal to the expected value.
   *        Exp. -> helper.getById(id).toBeGreaterThanOrEqual(25)
   *
   * @param {number} arg
   */
  toBeGreaterThanOrEqual(arg) {
    this.doExpect('toBeGreaterThanOrEqual', this.getValue(false, true), false, false, arg);
  }
  /**
   * Test: To Be InstanceOf
   *    -: expect the actual to be an instance of the expected class
   *        Exp.  -> helper.getById(id).toBeInstanceOf(String)
   *              -> helper.getById(id).toBeInstanceOf(Number)
   *
   * @param arg
   */
  toBeInstanceOf(arg) {
    this.doExpect('toBeInstanceOf', false, false, false, arg);
  }
  /**
   * Test: To Be Less Than
   *    -: expect the actual value to be less than the expected value.
   *        Exp. -> helper.getById(id).toBeLessThan(0)
   *
   * @param {number} arg
   */
  toBeLessThan(arg) {
    this.doExpect('toBeLessThan', false, false, false, arg);
  }
  /**
   * Test: To Be Less Than Or Equal
   *    -: expect the actual value to be less than or equal to the expected value.
   *        Exp. -> helper.getById(id).toBeLessThanOrEqual(123)
   *
   * @param {number} arg
   */
  toBeLessThanOrEqual(arg) {
    this.doExpect('toBeLessThanOrEqual', false, false, false, arg);
  }
  /**
   * Test: To Be NaN
   *    -: expect the actual value to be NaN (Not a Number).
   *        Exp. -> helper.getById(id).toBeNaN()
   *
   */
  toBeNaN() {
    this.doExpect('toBeNaN');
  }
  /**
   * Test: To Be Negative Infinity
   *    -: expect the actual value to be -Infinity (-infinity).
   *        Exp. -> helper.getById(id).toBeNegativeInfinity()
   *
   */
  toBeNegativeInfinity() {
    this.doExpect('toBeNegativeInfinity');
  }
  /**
   * Test: To Be Null
   *    -: expect the actual value to be null.
   *        Exp. -> helper.getById(id).toBeNull()
   *
   */
  toBeNull() {
    this.doExpect('toBeNull');
  }
  /**
   * Test: To Be Positive Infinity
   *    -: expect the actual value to be Infinity (infinity).
   *        Exp. -> helper.getById(id).toBePositiveInfinity()
   *
   */
  toBePositiveInfinity() {
    this.doExpect('toBePositiveInfinity');
  }
  /**
   * Test: To Be True
   *    -: expect the actual value to be true.
   *        Exp. -> helper.getById(id).toBeTrue()
   *
   */
  toBeTrue() {
    this.doExpect('toBeTrue');
  }
  /**
   * Test: To Be Undefined
   *    -: expect the actual value to be undefined.
   *        Exp. -> helper.getById(id).toBeUndefined()
   *
   */
  toBeUndefined() {
    this.doExpect('toBeUndefined');
  }
  /**
   * Test: To be defined
   *    -: expect the actual value to be defined. (Not undefined)
   *        Exp. -> helper.getById(id).toBeDefined()
   */
  toBeDefined() {
    this.doExpect('toBeDefined');
  }
  /**
   * Test: To Contain
   *    -: expect the actual value to contain a specific value.
   *        Exp.  -> helper.getById(id).toContain(anElement)
   *              -> helper.getById(id).toContain(substring)
   *
   * @param {number | string} arg
   * @param {boolean} stripTags
   */
  toContain(arg, stripTags = false) {
    this.doExpect('toContain', false, stripTags, false, arg);
  }
  /**
   * Test: To Match
   *    -: expect the actual value to match a regular expression
   *        Exp.  -> helper.getById(id).toMatch(/string$/)
   *              -> helper.getById(id).toMatch("her")
   *
   * @param {number | string} arg
   * @param {boolean} stripTags
   */
  toMatch(arg, stripTags = false) {
    this.doExpect('toMatch', false, stripTags, false, arg);
  }
  /**
   * Test: To Be
   *    -: expect the actual value to be === to the expected value.
   *        Exp. -> helper.getById(id).toBe('Universal Studios')
   *
   * @param {any} arg
   * @param {boolean} fakeTest
   * @param {boolean} stripTags
   * @return {any}
   */
  toBe(arg, fakeTest = true, stripTags = false) {
    this.doExpect('toBe', false, stripTags, fakeTest, arg);
  }
  /**
   * Test: To Equal
   *    -: expect the actual value to be equal to the expected, using deep equality comparison.
   *        Exp. -> helper.getById(id).toEqual({a: 1})
   *
   * @param {any} arg
   * @param {boolean} fakeTest
   * @return {any}
   */
  toEqual(arg, fakeTest = true) {
    this.doExpect('toEqual', false, false, fakeTest, arg);
  }
  /**
   * Test: Length To Be
   *        Exp. -> helper.getById(id).lengthToBe(12)
   *
   * @param {number} value
   * @param {boolean} fakeTest
   */
  lengthToBe(value, fakeTest = true) {
    this.doExpect('toBe', this.el ? this.el.length : undefined, fakeTest, false, value);
  }
  /**
   * Test: To Be Truthy
   *    -: expect the element to be truthy.
   *        Exp. -> helper.getById(id).toBeTruthy()
   *
   */
  toBeTruthy() {
    this.expect(this.getElement()).toBeTruthy();
  }
  /**
   * Test: To Be Falsy
   *    -: expect the element to be falsy
   *        Exp. -> helper.getById(id).toBeFalsy()
   *
   * @return {any}
   */
  toBeFalsy() {
    this.expect(this.getElement()).toBeFalsy();
  }
  /**
   * Test: To Have Class
   *    -: expect the actual value to be a DOM element that has the expected class
   *        Exp. -> helper.getById(id).toHaveClass(className)
   */
  toHaveClass(expected) {
    this.expect(this.getElement()).toHaveClass(expected);
  }
  /**
   * Test: To be false
   *    -: expect the element to be false.
   *        Exp. -> helper.getById(id).toBeFalse()
   */
  toBeFalse() {
    this.expect(this.getElement()).toBeFalse();
  }
  /**
   * Change select option in the form
   * @param {string|number} keyIndex
   * @return {any}
   */
  changeSelectOption(keyIndex) {
    // Result variable
    let result;
    // If we got needed data
    if (this.el) {
      // Get element
      const el = this.getElement();
      // Change the select option value
      result = el.value = this.getSelectOption(keyIndex);
      // Dispatch event
      el.dispatchEvent(new Event('change'));
      // Detect changes
      this.detectChanges();
    }
    // Return entire element
    return result;
  }
  /**
   * Click on element
   */
  click() {
    // Detect changes
    this.detectChanges();
    // Click on element
    this.getElement().triggerEventHandler('click', null);
    // Detect changes again
    this.detectChanges();
  }
  /**
   * Get elements count
   * @return {number}
   */
  getLength() {
    return !this.el ? 0 : this.el.length;
  }
  /**
   * Native element: Get value
   * @param {boolean} stripTags
   * @param {boolean} isInt
   * @return {undefined|any}
   */
  getValue(stripTags = false, isInt = false) {
    const getContent = () => {
      const tagName = get(this.el, 'nativeElement.tagName', undefined);
      return get(this.el, 'nativeElement.' + (tagName && tagName.toLowerCase() === 'input' ? 'value' : 'textContent'), undefined);
    };
    return this.el ? stripTags || isInt ? stripTags ? this.stripHtmlTags(getContent()) : parseInt(getContent(), 10) : getContent() : undefined;
  }
  /**
   * Get native element
   * @return {undefined|any}
   */
  getElement() {
    return this.el ? get(this.el, 'nativeElement', undefined) : undefined;
  }
  /**
   * Get DOM element by class name
   *
   * Example: .getByClassName('mapInfoBlock')
   *          .getByClassName('mapInfoBlock', false, 'p')
   *          .getByClassName('mapInfoBlock', 'nativeElement.textContent')
   *          .getByClassName('mapInfoBlock', 'nativeElement.textContent', 'p')
   *          .getByClassName('mapInfoBlock', 'nativeElement.textContent', 'p', false)
   *          .getByClassName('mapInfoBlock', false, false, false)
   *              The last parameter "false" gonna return multiple items,
   *              cause by default "JasmineHelper" do work with a single element".
   *              Small performance protection for future.
   *
   * @param {string} className
   * @param {string | boolean} neededData
   * @param {string} tag
   * @param {boolean} isSingle
   * @return {any|this}
   */
  getByClassName(className, neededData = false, tag = '', isSingle = true) {
    // Get dom element
    const domElement = this.getDomElement(isString(tag) ? `${tag}.${className}` : `.${className}`, neededData, isSingle);
    // Return result depends if we gonna use native element manually
    return neededData ? domElement : this;
  }
  /**
   * Get DOM element by ID
   *
   * Example: .getById('mapInfoBlock')
   *          .getById('mapInfoBlock', false, 'p')
   *          .getById('mapInfoBlock', 'nativeElement.textContent')
   *          .getById('mapInfoBlock', 'nativeElement.textContent', 'p')
   *          .getById('mapInfoBlock', 'nativeElement.textContent', 'p', false)
   *          .getById('mapInfoBlock', false, false, false)
   *              The last parameter "false" gonna return multiple items,
   *              cause by default "JasmineHelper" do work with a single element".
   *              Small performance protection for future.
   *
   * @param {string} idEl
   * @param {string | boolean} neededData
   * @param {string} tag
   * @param {boolean} isSingle
   * @return {any|this}
   */
  getById(idEl, neededData = false, tag = '', isSingle = true) {
    // Get dom element
    const domElement = this.getDomElement(isString(tag) ? `${tag}#${idEl}` : `#${idEl}`, neededData, isSingle);
    // Return result depends if we gonna use native element manually
    return neededData ? domElement : this;
  }
  /**
   * Get by selector
   *
   * Example: .getBySelector('h3')
   *          .getBySelector('h3', 'nativeElement.textContent')
   *          .getBySelector('h3', 'nativeElement.textContent', false)
   *          .getBySelector('h3', false, false)
   *
   * @param {string} tag
   * @param {string | boolean} neededData
   * @param {boolean} isSingle
   * @return {any|this}
   */
  getByTag(tag, neededData = false, isSingle = true) {
    // Get dom element
    const domElement = this.getDomElement(tag, neededData, isSingle);
    // Return result depends if we gonna use native element manually
    return neededData ? domElement : this;
  }
  /**
   * Get DOM element by attributes
   *
   * Example: .getByAttr('p', 'data-aui', 'AttractionExperiences')
   *          .getByAttr('p', 'data-aui', 'AttractionExperiences', 'nativeElement.textContent')
   *          .getByAttr('p', 'data-aui', 'AttractionExperiences', 'nativeElement.textContent', false)
   *          .getByAttr('p', 'data-aui', 'AttractionExperiences', false, false)
   *              The last parameter "false" gonna return multiple items,
   *              cause by default "JasmineHelper" do work with a single element".
   *              Small performance protection for future.
   *
   * @param {string} tag
   * @param {string} attrKey
   * @param {string} attrValue
   * @param {string | boolean} neededData
   * @param {boolean} isSingle
   * @return {any|this}
   */
  getByAttr(tag, attrKey, attrValue, neededData = false, isSingle = true) {
    // Get dom element
    const domElement = this.getDomElement(`${tag}[${attrKey}=${attrValue}]`, neededData, isSingle);
    // Return result depends if we gonna use native element manually
    return neededData ? domElement : this;
  }
  /**
   * Get dom element by class name, id or attributes
   * @param {string} stringPath
   * @param {string | boolean} neededData
   * @param {boolean} isSingle
   * @return {any|this}
   */
  getDomElement(stringPath, neededData, isSingle = true) {
    // Detect changes before any changes with a DOM
    this.detectChanges();
    // Get DOM element
    const domElement = this.el = this.de[isSingle ? 'query' : 'queryAll'](By.css(stringPath));
    // Return result
    return domElement && neededData && isString(neededData) ? get(domElement, neededData) : this;
  }
  /**
   * Do expect test
   * @param {string} funcName
   * @param {any} expected
   * @param {boolean} stripTags
   * @param {boolean} fakeTest
   * @param {[]} args
   */
  doExpect(funcName, expected, stripTags, fakeTest, ...args) {
    const getExpected = () => {
      return expected ? stripTags ? this.stripHtmlTags(expected) : expected : this.getValue(stripTags);
    };
    if (args && typeof args[0] !== 'undefined') {
      for (const x in args) {
        if (args.hasOwnProperty(x)) {
          args[x] = stripTags ? this.stripHtmlTags(args[x]) : args[x];
        }
      }
      this.expect(getExpected())[funcName](...args);
    } else {
      if (fakeTest) {
        this.expect(true).toBe(true);
      } else {
        this.expect(getExpected()).toBe(...args);
      }
    }
  }
  /**
   * Get select option value
   * @param {string | number | boolean} key
   * @return {string | number | boolean}
   */
  getSelectOption(key) {
    // If we dont' have DOM of native element
    if (!this.el) {
      return undefined;
    }
    // Get element
    return key ? this.getElement()['options'][key]['value'] : this.getElement()['options'];
  }
  /**
   * Strip tags
   * @param {string} str
   * @return {string}
   */
  stripHtmlTags(str) {
    return Number.isInteger(str) ? str : str.replace(/(<([^>]+)>)/gi, '').trim();
  }
  /**
   * Strip lines
   * @param {string} str
   * @return {string}
   */
  stripLines(str) {
    return Number.isInteger(str) ? str : str.replace(/\r?\n|\r/g, '').trim();
  }
  /**
   * Fake Test
   * @param expVal
   * @param value
   */
  fakeTest(expVal = true, value = true) {
    this.expect(expVal).toBe(value);
  }
  /**t
   * Detect changes
   */
  detectChanges() {
    this.fixture.detectChanges();
  }
}

/**
 * Wait for element to appear in dom, just needs a class or an id passed in, and promise/await for the element to appear.
 * You can optionally pass in a parent element, instead of watching the whole document for better performance.
 * Will do an interval check the first 10 seconds for performance
 * Observer debounced by 100 milliseconds for performance.
 * @param {string} selector element selector: '.class' or '#id' form
 * @param {Element} parentElement document.body or parent element you want to watch.
 * @param {number} intervalTime change time to wait for changes. Default 1000
 * @param {number} debounceTime change time to wait for changes. Default 100
 * @param {number} subtree if false the observer, will only look at child elements, this is best for performance.
 */
function waitForElement(selector, parentElement, intervalTime, debounceTime, subtree = true) {
  return new Promise((resolve, reject) => {
    const intervalCheck = intervalTime || 1000;
    const debounceCheck = debounceTime || 100;
    const parentNode = parentElement || document.body;
    const element = findElement(selector, parentNode);
    if (element) {
      resolve(element);
    } else {
      // lets try interval check first, if timeout completes (10 seconds)
      // We haven't found the item, so use mutation observer.
      const intervalTimer = interval(intervalCheck).pipe(takeUntil(timer(10000))).subscribe(() => {
        const element2 = findElement(selector, parentNode);
        if (element2) {
          resolve(element2);
          intervalTimer.unsubscribe();
        }
      }, error => {
        console.log('Error finding element:', error);
        reject(error);
      }, () => setupObserver());
      const setupObserver = () => {
        const observer = new MutationObserver(debounce(mutationList => {
          // Look for element within records
          const mutation = mutationList.reduce((_, record) => Array.from(record.addedNodes).find(node => node instanceof HTMLElement && node?.matches(selector)), undefined);
          // if above not found, try for regular select
          !resolveFunc(mutation) && resolveFunc(findElement(selector, parentNode));
        }, debounceCheck, {
          leading: true
        }));
        observer.observe(parentNode, {
          childList: true,
          subtree
        });
        const resolveFunc = found => {
          if (found) {
            resolve(found);
            observer.disconnect();
            return true;
          }
          return false;
        };
      };
    }
  });
}
/**
 * Watch for elements based on selector, then mutation debounced by 100 milliseconds for performance.
 * You can optionally pass in a parent element, instead of watching the whole document for better performance.
 * @param {string} selector element selector: '.class' or '#id' form
 * @param {Element} parentElement document.body or parent element you want to watch.
 * @param {number} debounceTime change time to wait for changes.
 */
function watchForElement(selector, parentElement, debounceTime) {
  const debounceWait = debounceTime || 100;
  const subject = new Subject();
  const parentNode = parentElement || document.body;
  // Send first set found if any
  const findElem = () => {
    const elements = findElements(selector, parentNode);
    elements.length && subject.next(elements);
  };
  const observer = new MutationObserver(debounce(() => requestAnimationFrame(() => findElem()), debounceWait));
  observer.observe(parentNode, {
    childList: true,
    subtree: true
  });
  // Replace with our own version that also disconnects subscription.
  subject['_oldUnsubscribe'] = subject.unsubscribe;
  subject.unsubscribe = () => {
    observer.disconnect();
    subject['_oldUnsubscribe']();
  };
  return subject;
}
/**
 * Watch for element attribute changes, debounce by 100 milliseconds to expect any changes.
 * @param {string} attribute attribute to watch for. Passing '' will look for all changes.
 * @param {Element} element document.body or parent element you want to watch.
 * @param {number} debounceTime change time to wait for changes.
 */
function watchForAttributeChange(attribute, element, debounceTime) {
  const debounceWait = debounceTime || 100;
  // let's send the first one
  const subject = new BehaviorSubject(element);
  // Only watch for specific attribute change
  const observer = new MutationObserver(debounce(mutationList => {
    if (attribute === '') {
      subject.next(element);
    } else {
      const mutation = mutationList.reduce((_, record) => record.attributeName === attribute ? record.target : undefined, undefined);
      mutation && subject.next(mutation);
    }
  }, debounceWait));
  observer.observe(element, {
    attributes: true,
    attributeFilter: attribute ? [attribute] : undefined
  });
  // Replace with our own version that also disconnects subscription.
  subject['_oldUnsubscribe'] = subject.unsubscribe;
  subject.unsubscribe = () => {
    observer.disconnect();
    subject['_oldUnsubscribe']();
  };
  return subject;
}
function findElement(selector, parent) {
  return document.getElementById(selector) || parent.querySelector(selector);
}
function findElements(selector, parent) {
  return findElement(selector, parent) ? Array.from(parent.querySelectorAll(selector)) : [];
}
function restoreScrollPosition(callback) {
  const currentScrollY = window.pageYOffset || window.scrollY;
  callback().finally(() => {
    window.scrollTo(0, currentScrollY);
  });
}
function sendEvent(name, detail, options = {}) {
  return window.dispatchEvent(new CustomEvent(name, {
    detail,
    ...options
  }));
}

/*
 * Public API Surface of app-utils
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APP_ENV_ENV_DEFAULTS, AppEnvironmentService, AppUtilsModule, AutoSubscription, BaseComponent, CacheKeys, DEFAULT_RETRY_STRATEGY, JasmineBaseHelper, ServiceGuard, SubscriptionComponent, checkForFallback, devLogger, genericRetryStrategy, getValuesFromPath, param, paramArray, paramBool, paramNum, restoreScrollPosition, sendEvent, waitForElement, watchForAttributeChange, watchForElement };
